import { useEffect, useState } from "react";
import { Sparkles, Pencil, Check } from "@/lib/icons";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ModalClose } from "@/components/ui/ModalClose";

export function Question({ q, value, onChange }) {
  const toggle = (opt) => {
    if (!q.multi) return onChange([opt]);
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div data-testid={`rp-q-${q.key}`}>
      <div className="text-sm font-semibold text-stone-900 mb-2">{q.q}{q.multi && <span className="text-xs font-normal text-stone-500"> · pick any</span>}</div>
      <div className="flex flex-wrap gap-1.5">
        {q.options.map((opt) => {
          const on = value.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)} data-testid={`rp-opt-${q.key}-${opt.replace(/[^a-z]+/g, "-")}`} aria-pressed={on}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-amber-600"}`}>
              {on && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RewardProfileForm({ profile, onSaved, onCancel, saveLabel = "Save", compact = false }) {
  const [answers, setAnswers] = useState(profile.answers || {});
  const [text, setText] = useState(profile.text || {});
  const [notes, setNotes] = useState(profile.notes || "");
  const [busy, setBusy] = useState(false);
  const filled = Object.values(text).filter((v) => v?.trim()).length + (answers.avoid?.length ? 1 : 0);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/me/reward-profile", { answers, text, notes });
      toast.success("Saved — your next rewards will use it.");
      onSaved(r.data);
    } catch { toast.error("Couldn't save right now."); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="reward-profile-form">
      <div className={`space-y-4 ${compact ? "max-h-[40vh] overflow-y-auto pr-1 pb-1" : ""}`}>
        {(profile.text_prompts || []).map((t) => (
          <div key={t.key} data-testid={`rp-text-${t.key}`}>
            <label className="text-sm font-semibold text-stone-900 block mb-1.5">{t.q}</label>
            <textarea value={text[t.key] || ""} onChange={(e) => setText((x) => ({ ...x, [t.key]: e.target.value }))} maxLength={300} rows={2} placeholder={t.hint} data-testid={`rp-text-input-${t.key}`}
              className="w-full rounded-2xl border border-stone-300 px-3 py-2 text-sm outline-none focus:border-amber-600 leading-relaxed" />
          </div>
        ))}
        {profile.questions.map((q) => <Question key={q.key} q={q} value={answers[q.key] || []} onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))} />)}
        <div>
          <label className="text-sm font-semibold text-stone-900 block mb-1.5">Anything else to steer clear of, or to know? <span className="text-xs font-normal text-stone-500">optional</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={400} rows={2} data-testid="rp-notes" placeholder="e.g. no food rewards please; I'm saving money this year; mornings are my best time"
            className="w-full rounded-2xl border border-stone-300 px-3 py-2 text-sm outline-none focus:border-amber-600" />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-stone-500" data-testid="rp-filled">{filled}/{(profile.text_prompts || []).length + 1} filled</span>
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} data-testid="rp-cancel" className="px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">Cancel</button>}
          <button onClick={save} disabled={busy || filled === 0} data-testid="rp-save" className="amber-btn text-sm">{busy ? "Saving…" : saveLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function RewardProfileModal({ profile, onClose, onSaved }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4 overflow-y-auto" data-testid="reward-profile-modal" onClick={onClose}>
      <div className="warm-card relative p-6 w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()}>
        <ModalClose onClose={onClose} testid="reward-profile-close" />
        <div className="mb-4 pr-10">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Reward profile</div>
          <h3 className="font-display text-xl font-bold text-stone-900 mt-1">Tell us what you love — in your own words</h3>
          <p className="text-xs text-stone-500 mt-1">The more specific, the more creative your rewards get. Names and places are removed before anything reaches the AI; your coach can see the full answers.</p>
        </div>
        <RewardProfileForm profile={profile} onCancel={onClose} onSaved={(p) => { onSaved(p); onClose(); }} saveLabel="Save answers" />
      </div>
    </div>
  );
}

export function RewardProfileCard() {
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/me/reward-profile").then((r) => setProfile(r.data)); }, []);
  if (!profile) return null;
  const chips = [...Object.values(profile.text || {}).flatMap((v) => v.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean)), ...(profile.answers?.avoid || []).map((a) => `avoid ${a}`)];
  return (
    <section className="warm-card p-5 mb-4" data-testid="reward-profile-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Sparkles className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold text-stone-900">Your reward profile <span className="text-xs font-sans font-semibold text-stone-500" data-testid="rp-progress">{profile.answered}/{profile.total} answered</span></h3>
            {chips.length === 0 ? (
              <p className="text-sm text-stone-600 mt-1">Tell us what you genuinely enjoy, what feels like a treat, and what to steer clear of — in your own words. Rewards get noticeably more <em>you</em>.</p>
            ) : (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="rp-chips">{chips.slice(0, 10).map((c) => <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200">{c}</span>)}{chips.length > 10 && <span className="text-[11px] text-stone-500">+{chips.length - 10} more</span>}</div>
            )}
          </div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="rp-edit" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-stone-900 text-white hover:bg-amber-700 shrink-0">
          <Pencil className="w-3.5 h-3.5" /> {chips.length ? "Edit" : "Tell us what you love"}
        </button>
      </div>
      {open && <RewardProfileModal profile={profile} onClose={() => setOpen(false)} onSaved={(p) => setProfile((cur) => ({ ...cur, ...p }))} />}
    </section>
  );
}

export function ClinicianRewardProfile({ clientId }) {
  const [p, setP] = useState(null);
  useEffect(() => { setP(null); api.get(`/clinician/client/${clientId}/reward-profile`).then((r) => setP(r.data)); }, [clientId]);
  if (!p) return null;
  return (
    <section className="warm-card p-6" data-testid="clinician-reward-profile">
      <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-amber-700" /><h3 className="font-display text-lg font-bold text-stone-900">Reward profile</h3>
        <span className="text-xs text-stone-500">{p.answered}/{p.total} answered{p.updated_at && ` · updated ${new Date(p.updated_at).toLocaleDateString()}`}</span></div>
      <p className="text-xs text-stone-500 mb-4">What the member told us makes a reward land. Use it when suggesting rewards or framing wins in session.</p>
      {p.answered === 0 && !p.notes && <p className="text-sm text-stone-500" data-testid="crp-empty">They haven't filled in their reward profile yet — you could mention it next session.</p>}
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {(p.text_prompts || []).filter((t) => p.text?.[t.key]).map((t) => (
          <div key={t.key} data-testid={`crp-text-${t.key}`} className="rounded-2xl bg-amber-50/60 border border-amber-100 p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t.q.split("?")[0]}</div>
            <div className="text-sm text-stone-800 mt-1">{p.text[t.key]}</div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {p.questions.filter((q) => p.answers[q.key]?.length).map((q) => (
          <div key={q.key} data-testid={`crp-${q.key}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{q.q}</div>
            <div className="flex flex-wrap gap-1 mt-1">{p.answers[q.key].map((a) => <span key={a} className={`text-xs px-2 py-0.5 rounded-full border ${q.key === "avoid" ? "bg-rose-50 text-rose-900 border-rose-200" : "bg-amber-50 text-amber-900 border-amber-200"}`}>{a}</span>)}</div>
          </div>
        ))}
      </div>
      {p.notes && <div className="mt-4 rounded-2xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-800" data-testid="crp-notes"><span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">In their words</span>{p.notes}</div>}
    </section>
  );
}
